import { useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { useCryptoStore } from '../../stores/cryptoStore';
import { shareKeysWithNewMember, type ShareMemberKeysResult } from '../../lib/share-member-keys';
import { unsubscribeFromWebPush } from '../../lib/web-push';
import { usePresenceStore } from '../../stores/presenceStore';
import { useSocketStore, disconnectSocket } from '../../lib/socket';
import { applyTheme, getStoredTheme, type Theme } from '../../lib/theme';
import CreateChannelModal from '../channel/CreateChannelModal';
import CreatePracticeModal from '../channel/CreatePracticeModal';
import NewChatModal from '../channel/NewChatModal';
import InviteModal from '../invite/InviteModal';

interface OrgData {
  id: string;
  name: string;
  slug: string;
  role: string;
}

interface ChannelData {
  id: string;
  name: string;
  type: string;
  topic: string | null;
  unreadCount?: number;
  dmPeerName?: string | null;
  dmPeerAvatarUrl?: string | null;
}

export default function Sidebar({ onClose }: { onClose?: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgSlug, channelId } = useParams();
  const isTodosActive = location.pathname === '/app/todos';
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const socketStatus = useSocketStore((s) => s.status);
  const getStatus = usePresenceStore((s) => s.getStatus);
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showCreatePractice, setShowCreatePractice] = useState(false);
  const [addMemberChannelId, setAddMemberChannelId] = useState<string | null>(null);
  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [theme, setTheme] = useState<Theme>(getStoredTheme);

  function toggleTheme() {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    setTheme(next);
  }

  const { data: orgs } = useQuery<OrgData[]>({
    queryKey: ['orgs'],
    queryFn: async () => {
      const { data } = await api.get('/api/v1/orgs');
      return data.data;
    },
  });

  // Server-side default workspace. Derived from /users/me rather than a
  // useState-once localStorage read — that ran before the auth store had
  // hydrated `user`, so the star looked unset after every restart. Server
  // truth also lets the Chrome extension and todos honour the same star.
  const { data: me } = useQuery<{ id: string; defaultOrgId: string | null }>({
    queryKey: ['me'],
    queryFn: async () => {
      const { data } = await api.get('/api/v1/users/me');
      return data.data;
    },
  });
  const defaultOrgId = me?.defaultOrgId ?? null;

  // URL slug wins, then the user's saved default workspace, then first-listed.
  const currentOrg =
    orgs?.find((o) => o.slug === orgSlug) || orgs?.find((o) => o.id === defaultOrgId) || orgs?.[0];

  async function toggleDefaultOrg() {
    if (!currentOrg) return;
    const next = defaultOrgId === currentOrg.id ? null : currentOrg.id;
    try {
      await api.patch('/api/v1/users/me', { defaultOrgId: next });
      // Re-render the star from server truth.
      queryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success(
        next ? `${currentOrg.name} is now your default workspace` : 'Default workspace cleared',
      );
    } catch {
      toast.error('Could not update your default workspace');
    }
  }

  const { data: channels, error: channelsError } = useQuery<ChannelData[]>({
    queryKey: ['channels', currentOrg?.id],
    queryFn: async () => {
      const { data } = await api.get(`/api/v1/orgs/${currentOrg!.id}/channels`);
      return data.data;
    },
    enabled: !!currentOrg?.id,
  });

  const { data: searchResults } = useQuery<{ id: string; displayName: string; email: string }[]>({
    queryKey: ['userSearch', currentOrg?.id, addMemberSearch],
    queryFn: async () => {
      const { data } = await api.get(
        `/api/v1/users/search?orgId=${currentOrg!.id}&q=${encodeURIComponent(addMemberSearch)}`,
      );
      return data.data;
    },
    enabled: !!currentOrg?.id && addMemberChannelId !== null,
  });

  const [shareHistory, setShareHistory] = useState(false);

  const addMemberMutation = useMutation({
    mutationFn: async ({
      channelId,
      userId,
      includeHistory,
    }: {
      channelId: string;
      userId: string;
      includeHistory: boolean;
    }): Promise<ShareMemberKeysResult> => {
      const { data: added } = await api.post<{ data: { joinedAt?: string } }>(
        `/api/v1/channels/${channelId}/members`,
        { userId },
      );
      const membershipJoinedAt = added?.data?.joinedAt;

      // Seal the channel key(s) to the new member on this device so they can
      // decrypt. The admin chose per join: current epoch only (reads from now
      // on) or every epoch this device holds (full history).
      const secretKey = useCryptoStore.getState().secretKey;
      if (!secretKey) return { status: 'failed' };
      return shareKeysWithNewMember(
        { api, secretKey },
        { channelId, userId, includeHistory, membershipJoinedAt: membershipJoinedAt ?? '' },
      );
    },
    onSuccess: (result, vars) => {
      switch (result.status) {
        case 'shared':
          toast.success(
            vars.includeHistory
              ? 'Member added with access to earlier messages'
              : 'Member added. They can read new messages from now on.',
          );
          if (result.skipped.length > 0) {
            toast(
              `${result.skipped.length} earlier key(s) this device does not hold were not shared.`,
              { icon: '⚠️' },
            );
          }
          break;
        case 'rolled':
          if (result.joinerCovered) {
            toast.success('Member added. They can read new messages from now on.');
          } else {
            // The fresh key was sealed to everyone who has encryption set up;
            // setting it up later does NOT retroactively give them a copy.
            toast(
              'Member added, but they have not set up encryption yet, so they cannot read anything until they do. After they sign in, open Key rotation and trigger a rotation to give them access from that point.',
              { icon: '⚠️' },
            );
          }
          break;
        case 'no-public-key':
          // Only reached on the history path. Triggering a rotation later would give
          // them a FRESH key (new messages only), not the history chosen here.
          toast(
            'Member added, but they have not set up encryption yet, so they cannot read anything. To share the earlier messages, remove and re-add them after they sign in.',
            { icon: '⚠️' },
          );
          break;
        default:
          toast(
            'Member added, but their encryption key could not be shared. Open Key rotation for this channel and trigger a rotation.',
            { icon: '⚠️' },
          );
      }
      setAddMemberChannelId(null);
      setAddMemberSearch('');
      setShareHistory(false);
      queryClient.invalidateQueries({ queryKey: ['channels', currentOrg?.id] });
    },
    onError: () => toast.error('Failed to add member'),
  });

  function displayName(name: string) {
    return name.replace(/-/g, ' ');
  }

  const filteredChannels = useMemo(() => {
    if (!channels) return { groups: [], dm: [] };
    const f = filter.toLowerCase();
    const pub = channels.filter(
      (c) => c.type === 'PRIVATE' && (!f || c.name.toLowerCase().includes(f)),
    );
    const dm = channels.filter(
      (c) => c.type === 'DM' && (!f || (c.dmPeerName ?? c.name).toLowerCase().includes(f)),
    );
    return { groups: pub, dm };
  }, [channels, filter]);

  async function handleLogout() {
    disconnectSocket();
    // Awaited, not fire-and-forget: the DELETE is authenticated with the live
    // accessToken read from the store, so letting logout() clear it first
    // raced the request into a 401 and left the subscription registered —
    // a signed-out browser that keeps waking up with colleagues' names on it.
    // unsubscribeFromWebPush swallows its own failures, so this cannot block
    // sign-out.
    await unsubscribeFromWebPush();
    api.post('/api/v1/auth/logout').catch(() => {});
    logout();
    navigate('/login');
  }

  function handleChannelClick(ch: ChannelData) {
    const slug = currentOrg?.slug || orgSlug || 'default';
    navigate(`/app/${slug}/${ch.id}`);
    onClose?.();
  }

  async function handleOrgChange(org: OrgData) {
    try {
      const { data } = await api.get(`/api/v1/orgs/${org.id}/channels`);
      const chs = data.data as ChannelData[];
      if (chs.length > 0) {
        navigate(`/app/${org.slug}/${chs[0].id}`);
      } else {
        navigate(`/app/${org.slug}`);
      }
    } catch {
      navigate(`/app/${org.slug}`);
    }
  }

  const userStatus = user?.id ? getStatus(user.id) : 'ONLINE';
  const userStatusColor =
    userStatus === 'ONLINE'
      ? 'bg-accent-green'
      : userStatus === 'AWAY'
        ? 'bg-accent-amber'
        : 'bg-warm-gray-400';

  const connDot =
    socketStatus === 'connected'
      ? 'bg-accent-green'
      : socketStatus === 'reconnecting'
        ? 'bg-accent-amber'
        : 'bg-accent-coral';
  const connText =
    socketStatus === 'connected'
      ? 'Connected'
      : socketStatus === 'reconnecting'
        ? 'Reconnecting...'
        : 'Disconnected';

  return (
    <aside className="flex h-full w-64 flex-col bg-sidebar text-sidebar-text">
      {/* Org switcher section */}
      <div className="border-b border-sidebar-border px-4 py-4">
        <div className="flex items-center gap-1.5">
          <select
            aria-label="Switch workspace"
            className="w-full min-w-0 flex-1 rounded-lg bg-white/[0.06] px-3 py-2 text-body-sm font-semibold text-sidebar-text outline-none transition-colors duration-150 hover:bg-white/[0.1] focus:ring-1 focus:ring-primary/40"
            style={{ colorScheme: 'dark' }}
            value={currentOrg?.id || ''}
            onChange={(e) => {
              const org = orgs?.find((o) => o.id === e.target.value);
              if (org) handleOrgChange(org);
            }}
          >
            {orgs?.map((org) => (
              <option
                key={org.id}
                value={org.id}
                style={{ backgroundColor: '#1e1b2a', color: '#f0ebe4' }}
              >
                {org.id === defaultOrgId ? `${org.name} ★` : org.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={toggleDefaultOrg}
            aria-label={
              currentOrg?.id === defaultOrgId
                ? 'Clear default workspace'
                : 'Set as default workspace'
            }
            aria-pressed={currentOrg?.id === defaultOrgId}
            title={
              currentOrg?.id === defaultOrgId
                ? 'Default workspace — click to clear'
                : 'Set as default workspace'
            }
            className={`shrink-0 rounded-lg p-2 transition-colors duration-150 hover:bg-white/[0.1] ${
              currentOrg?.id === defaultOrgId
                ? 'text-accent-amber'
                : 'text-sidebar-text/40 hover:text-sidebar-text'
            }`}
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill={currentOrg?.id === defaultOrgId ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.48 3.5c.17-.4.87-.4 1.04 0l2.13 5.11 5.52.44c.44.04.62.58.28.86l-4.2 3.6 1.28 5.38c.1.43-.36.77-.74.54L12 16.55l-4.79 2.88c-.38.23-.84-.11-.74-.54l1.28-5.38-4.2-3.6a.47.47 0 01.28-.86l5.52-.44 2.13-5.1z"
              />
            </svg>
          </button>
        </div>
        {currentOrg && (currentOrg.role === 'OWNER' || currentOrg.role === 'ADMIN') && (
          <button
            onClick={() => setShowInvite(true)}
            className="mt-2 flex w-full items-center gap-2 rounded-lg bg-white/[0.04] px-3 py-1.5 text-caption font-medium text-sidebar-text/70 transition-all duration-150 hover:bg-white/[0.08] hover:text-sidebar-text"
          >
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
            Invite people
          </button>
        )}
        <button
          onClick={() => setShowCreatePractice(true)}
          className="mt-1 flex w-full items-center gap-2 rounded-lg bg-white/[0.04] px-3 py-1.5 text-caption font-medium text-sidebar-text/70 transition-all duration-150 hover:bg-white/[0.08] hover:text-sidebar-text"
        >
          <svg
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Create practice
        </button>
        <button
          onClick={() => {
            navigate('/join');
            onClose?.();
          }}
          className="mt-1 flex w-full items-center gap-2 rounded-lg bg-white/[0.04] px-3 py-1.5 text-caption font-medium text-sidebar-text/70 transition-all duration-150 hover:bg-white/[0.08] hover:text-sidebar-text"
        >
          <svg
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
            />
          </svg>
          Join an organization
        </button>
      </div>

      {/* Quick nav section */}
      <div className="border-b border-sidebar-border px-2 py-2">
        <button
          onClick={() => {
            navigate('/app/todos');
            onClose?.();
          }}
          className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-body-sm transition-all duration-150 ${
            isTodosActive
              ? 'border-l-[3px] border-primary bg-primary/10 pl-[5px] font-medium text-primary'
              : 'text-sidebar-text/60 hover:bg-white/[0.05] hover:text-sidebar-text/90'
          }`}
        >
          <svg
            className="h-4 w-4 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
          To-do list
        </button>
        <button
          onClick={() => {
            navigate('/app/recent-files');
            onClose?.();
          }}
          className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-body-sm transition-all duration-150 ${
            location.pathname === '/app/recent-files'
              ? 'border-l-[3px] border-primary bg-primary/10 pl-[5px] font-medium text-primary'
              : 'text-sidebar-text/60 hover:bg-white/[0.05] hover:text-sidebar-text/90'
          }`}
        >
          <svg
            className="h-4 w-4 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
            />
          </svg>
          Recent Files
        </button>
        {/* The extension is what the product is for — a permanent way back to
            the install page, not just a one-off onboarding prompt, because
            staff join on one machine and later sit down at another. Opens in a
            new tab so an in-progress conversation is not navigated away from. */}
        <a
          href="/extension"
          target="_blank"
          rel="noreferrer noopener"
          onClick={() => onClose?.()}
          className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-body-sm text-sidebar-text/60 transition-all duration-150 hover:bg-white/[0.05] hover:text-sidebar-text/90"
        >
          <svg
            className="h-4 w-4 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2v-5m-9 2L20 4m0 0h-5m5 0v5"
            />
          </svg>
          Chrome extension
        </a>
      </div>

      {/* Filter input */}
      <div className="px-3 py-2">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full rounded-lg bg-white/[0.06] px-3 py-2 text-caption text-sidebar-text placeholder:text-sidebar-text-muted/70 outline-none transition-colors duration-150 focus:bg-white/[0.09] focus:ring-1 focus:ring-primary/30"
          placeholder="Filter conversations..."
        />
      </div>

      {/* Channel list */}
      <div className="flex-1 overflow-y-auto px-2 py-1 scrollbar-thin">
        {channelsError &&
          (() => {
            // X1: surface MFA gate vs generic error so AHPRA users don't land
            // on a misleading "try again later" dead-end. The server returns
            // 403 { code: 'MFA_REQUIRED' } for practitioner accounts without
            // 2FA — show targeted copy + Settings CTA in that case.
            const err = channelsError as {
              response?: { status?: number; data?: { error?: { code?: string } } };
            };
            const code = err.response?.data?.error?.code;
            const isMfaGate = err.response?.status === 403 && code === 'MFA_REQUIRED';
            if (isMfaGate) {
              return (
                <div className="mx-2 my-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-caption text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300">
                  <p className="mb-1.5 font-semibold">Two-factor authentication required</p>
                  <p className="mb-2">
                    Set up 2FA in Settings to access channels. AHPRA-registered accounts must enable
                    2FA before messaging.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/app/settings');
                      onClose?.();
                    }}
                    className="rounded-md bg-amber-200 px-2.5 py-1 text-label font-semibold text-amber-900 hover:bg-amber-300 dark:bg-amber-800/60 dark:text-amber-100 dark:hover:bg-amber-700/60"
                  >
                    Set up 2FA
                  </button>
                </div>
              );
            }
            return (
              <div className="px-3 py-2 text-caption text-accent-coral">
                Failed to load channels. Please try again later.
              </div>
            );
          })()}
        {filteredChannels.dm.length > 0 && (
          <div className="mb-3">
            <div className="mb-1 flex items-center justify-between px-2">
              <h3 className="text-label font-medium uppercase tracking-wider text-sidebar-text-muted">
                Direct Messages
              </h3>
              <button
                onClick={() => setShowNewChat(true)}
                aria-label="New direct message"
                className="flex h-6 w-6 items-center justify-center rounded text-sidebar-text-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-primary"
                title="New message"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </button>
            </div>
            {filteredChannels.dm.map((ch) => (
              <button
                key={ch.id}
                onClick={() => handleChannelClick(ch)}
                className={`flex w-full items-center rounded-lg px-2 py-1.5 text-body-sm transition-all duration-150 ${
                  ch.id === channelId
                    ? 'border-l-[3px] border-primary bg-primary/10 pl-[5px] font-medium text-sidebar-text'
                    : 'text-sidebar-text/60 hover:bg-white/[0.05] hover:text-sidebar-text/90'
                }`}
              >
                <span className="relative mr-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/80 text-label font-bold text-white shadow-sm overflow-hidden">
                  {ch.dmPeerAvatarUrl ? (
                    <img
                      src={ch.dmPeerAvatarUrl}
                      alt=""
                      className="h-7 w-7 rounded-full object-cover"
                    />
                  ) : (
                    ch.dmPeerName?.[0]?.toUpperCase() || '?'
                  )}
                  <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-[1.5px] border-sidebar bg-accent-green" />
                </span>
                <span className={ch.unreadCount ? 'font-semibold text-sidebar-text' : ''}>
                  {ch.dmPeerName || displayName(ch.name)}
                </span>
                {ch.unreadCount ? (
                  <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent-coral px-1.5 text-label font-bold text-white">
                    {ch.unreadCount}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        )}

        <div className="mb-3">
          <div className="mb-1 flex items-center justify-between px-2">
            <h3 className="text-label font-medium uppercase tracking-wider text-sidebar-text-muted">
              Groups
            </h3>
            <div className="flex items-center gap-1">
              {filteredChannels.dm.length === 0 && (
                <button
                  onClick={() => setShowNewChat(true)}
                  aria-label="New direct message"
                  className="flex h-6 w-6 items-center justify-center rounded text-sidebar-text-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-primary"
                  title="New message"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </button>
              )}
              <button
                onClick={() => setShowCreate(true)}
                aria-label="Create group"
                className="flex h-6 w-6 items-center justify-center rounded text-sidebar-text-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-primary"
                title="Create group"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>
          {filteredChannels.groups.map((ch: ChannelData) => (
            <div key={ch.id} className="group relative">
              <button
                onClick={() => handleChannelClick(ch)}
                className={`flex w-full items-center rounded-lg px-2 py-1.5 text-body-sm transition-all duration-150 ${
                  ch.id === channelId
                    ? 'border-l-[3px] border-primary bg-primary/10 pl-[5px] font-medium text-sidebar-text'
                    : 'text-sidebar-text/60 hover:bg-white/[0.05] hover:text-sidebar-text/90'
                }`}
              >
                <span className={ch.unreadCount ? 'font-semibold text-sidebar-text' : ''}>
                  {displayName(ch.name)}
                </span>
                {ch.unreadCount ? (
                  <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent-coral px-1.5 text-label font-bold text-white">
                    {ch.unreadCount}
                  </span>
                ) : null}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShareHistory(false);
                  setAddMemberChannelId(addMemberChannelId === ch.id ? null : ch.id);
                  setAddMemberSearch('');
                }}
                aria-label="Add person to channel"
                className="absolute right-1 top-1/2 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-sidebar-text-muted transition-colors duration-150 hover:bg-white/[0.08] hover:text-primary group-hover:flex"
                title="Add person"
              >
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                  />
                </svg>
              </button>
              {addMemberChannelId === ch.id && (
                <div className="absolute left-0 right-0 top-full z-30 mt-1 rounded-lg border border-sidebar-border bg-sidebar-hover p-2 shadow-warm-lg">
                  <input
                    value={addMemberSearch}
                    onChange={(e) => setAddMemberSearch(e.target.value)}
                    className="mb-1.5 w-full rounded-md bg-white/[0.06] px-2 py-1.5 text-caption text-sidebar-text placeholder:text-sidebar-text-muted/70 outline-none transition-colors duration-150 focus:ring-1 focus:ring-primary/30"
                    placeholder="Search by name or email..."
                    autoFocus
                  />
                  <label className="mb-1.5 flex cursor-pointer items-start gap-2 px-1 text-caption text-sidebar-text/80">
                    <input
                      type="checkbox"
                      checked={shareHistory}
                      onChange={(e) => setShareHistory(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      Also share earlier messages
                      <span className="block text-label text-sidebar-text-muted">
                        Off: they read new messages only. On: they can also read this channel&apos;s
                        history.
                      </span>
                    </span>
                  </label>
                  <div className="max-h-32 overflow-y-auto">
                    {searchResults
                      ?.filter((u) => u.id !== user?.id)
                      .map((u) => (
                        <button
                          key={u.id}
                          onClick={() =>
                            addMemberMutation.mutate({
                              channelId: ch.id,
                              userId: u.id,
                              includeHistory: shareHistory,
                            })
                          }
                          className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-caption text-sidebar-text/70 transition-colors duration-150 hover:bg-white/[0.06] hover:text-sidebar-text"
                        >
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/80 text-label font-bold text-white shadow-sm">
                            {u.displayName[0]?.toUpperCase()}
                          </span>
                          <span className="truncate">{u.displayName}</span>
                        </button>
                      ))}
                    {searchResults?.filter((u) => u.id !== user?.id).length === 0 && (
                      <p className="px-2 py-1 text-caption text-sidebar-text-muted">No results</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* User profile section */}
      <div className="border-t border-sidebar-border px-4 py-3">
        <div className="flex items-center">
          <div
            className="relative mr-3 flex h-9 w-9 flex-shrink-0 cursor-pointer items-center justify-center rounded-xl bg-primary/80 text-body-sm font-bold text-white shadow-sm overflow-hidden transition-transform duration-150 hover:scale-105"
            onClick={() => navigate('/app/settings')}
          >
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="h-9 w-9 rounded-xl object-cover" />
            ) : (
              user?.displayName?.[0]?.toUpperCase() || '?'
            )}
            <span
              className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-[1.5px] border-sidebar ${userStatusColor}`}
            />
          </div>
          <div className="flex-1 overflow-hidden">
            <p className="truncate text-body-sm font-semibold text-sidebar-text">
              {user?.displayName}
            </p>
            <div className="flex items-center gap-1.5">
              <span className={`h-1.5 w-1.5 rounded-full ${connDot}`} />
              <span className="text-label text-sidebar-text-muted">{connText}</span>
            </div>
          </div>
          <button
            onClick={() => {
              navigate('/app/settings');
              onClose?.();
            }}
            className="rounded-lg p-1.5 text-sidebar-text/50 transition-colors duration-150 hover:bg-white/[0.06] hover:text-sidebar-text"
            title="Settings"
            aria-label="Settings"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>
          <button
            onClick={toggleTheme}
            className="rounded-lg p-1.5 text-sidebar-text/50 transition-colors duration-150 hover:bg-white/[0.06] hover:text-sidebar-text"
            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          >
            {theme === 'dark' ? (
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            ) : (
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>
            )}
          </button>
          <button
            onClick={handleLogout}
            className="ml-1 rounded-lg p-1.5 text-sidebar-text/50 transition-colors duration-150 hover:bg-white/[0.06] hover:text-sidebar-text"
            title="Sign out"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
          </button>
        </div>
        <div className="mt-2 flex items-center justify-center">
          <button
            type="button"
            onClick={() => {
              navigate('/privacy');
              onClose?.();
            }}
            className="text-label text-sidebar-text-muted transition-colors duration-150 hover:text-sidebar-text"
          >
            Privacy
          </button>
        </div>
      </div>

      {currentOrg && (
        <>
          <CreateChannelModal
            orgId={currentOrg.id}
            open={showCreate}
            onClose={() => setShowCreate(false)}
          />
          <NewChatModal
            orgId={currentOrg.id}
            orgSlug={currentOrg.slug}
            open={showNewChat}
            onClose={() => setShowNewChat(false)}
          />
          <InviteModal
            orgId={currentOrg.id}
            orgName={currentOrg.name}
            open={showInvite}
            onClose={() => setShowInvite(false)}
          />
        </>
      )}
      <CreatePracticeModal
        open={showCreatePractice}
        onClose={() => setShowCreatePractice(false)}
        onCreated={(slug) => navigate(`/app/${slug}`)}
      />
    </aside>
  );
}
